import { AgGridReact } from '@mint-lib/ag-grid/react';
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type Ref,
} from 'react';

import {
  EventApiModule,
  ModuleRegistry,
  PaginationModule,
  RenderApiModule,
  RowApiModule,
  RowSelectionModule,
  ValidationModule,
  type ColDef,
  type GetRowIdParams,
  type GridApi,
  type GridReadyEvent,
} from '@mint-lib/ag-grid/community';
import {
  AGGridToolbar,
  LoadingCell,
  useAGGridToolbar,
} from '@mint-lib/ag-grid/components';
import {
  ContextMenuModule,
  ServerSideRowModelApiModule,
  ServerSideRowModelModule,
  ToolbarModule,
} from '@mint-lib/ag-grid/enterprise';
import { createDefaultColumn } from '@mint-lib/ag-grid/factory';
import { startOperation, trackAction } from '@mint-lib/datadog';
import { useDebounce } from '@mint-lib/hooks';
import { useTranslation } from '@mint-lib/i18n';
import { Edit, Send, TrashCan } from '@mint-lib/icons';
import { useProfile } from '@mint-lib/profile-helpers';
import { useQueryClient } from '@mint-lib/query-client';
import { cn } from '@mint-lib/tw-utils';
import { Auth } from '@mint-sdk/api-auth';
import { useMatch } from '@mint/routing';

import { USER_COLUMN_ID } from '../../api/queries/userColumnsConfig.js';
import { useUsersColumns } from '../../api/queries/usersColumns.js';
import { useUsersTags } from '../../api/queries/usersTags.js';
import {
  USER_TABLE_V2_CACHE_BLOCK_SIZE,
  USER_TABLE_V2_PAGE_SIZE,
} from '../../constants/userTableV2.js';
import { useModal } from '../../context/ModalContext/useModal.js';
import LabelModal from '../../organisms/LabelModal/LabelModal.jsx';
import ActionsCellSkeleton from '../tableCommons/ActionsCellSkeleton.jsx';
import NoResultsOverlay from '../tableCommons/NoResultsOverlay.jsx';
import TableSkeleton from '../tableCommons/TableSkeleton.jsx';
import ActionsCell from './components/ActionsCell.js';
import BulkActionConfirmationDialog, {
  type BulkUserAction,
} from './components/BulkActionConfirmationDialog.js';
import BulkEditModal from './components/BulkEditModal.js';
import DeleteUserConfirmationDialog from './components/DeleteUserConfirmationDialog.js';
import {
  createRowSelection,
  selectionColumn,
} from './components/SelectionColumn.js';
import { createUsersDatasource, type UserTableV2Row } from './datasource.js';
import {
  type SetUserUpdating,
  type UserTableV2GridContext,
} from './gridContext.js';
import useUserBulkActions from './hooks/useUserBulkActions.jsx';
import useUserRowActions from './hooks/useUserRowActions.jsx';
import { provideUserColumnFields } from './userColumns.jsx';
import './Table.css';

ModuleRegistry.registerModules([
  EventApiModule,
  PaginationModule,
  RowApiModule,
  RowSelectionModule,
  RenderApiModule,
  ValidationModule,
  ContextMenuModule,
  ServerSideRowModelModule,
  ServerSideRowModelApiModule,
  ToolbarModule,
]);

export type UserTableV2Ref = {
  refresh: () => Promise<void>;
};

type Props = {
  isReadonlyPermission: boolean;
  onEditClick: (user: UserTableV2Row) => void;
  tabs?: { id: string; name: string }[];
  ref?: Ref<UserTableV2Ref>;
};

// The toolbar is rendered by @mint-lib/ag-grid, so its buttons can't carry a
// `data-dd-action-name`. The reported name lives with the action instead of at
// each call site, and the Record means a new action can't be added without one.
const BULK_ACTION_NAMES: Record<BulkUserAction, string> = {
  activate: 'User management: bulk activate users',
  deactivate: 'User management: bulk deactivate users',
  sendInvitation: 'User management: bulk send invitations',
  editBulk: 'User management: bulk edit users',
  remove: 'User management: bulk remove users',
};

const useSearchParam = () => {
  const companyProfileMatch = useMatch({
    from: '/_secured/company-profile/users-management/user/',
    shouldThrow: false,
  });
  const agencyHubMatch = useMatch({
    from: '/_secured/agency-hub/users-management/user/',
    shouldThrow: false,
  });

  return companyProfileMatch?.search.search ?? agencyHubMatch?.search.search;
};

const UserTableV2 = ({
  isReadonlyPermission,
  onEditClick,
  tabs,
  ref,
}: Props) => {
  const { t } = useTranslation('@myn/permissions');
  const queryClient = useQueryClient();
  const { profile } = useProfile();
  const searchParam = useSearchParam();
  const { setOpen: setIsNewModalOpened } = useModal();
  const apiRef = useRef<GridApi<UserTableV2Row> | null>(null);
  const { gridApi, onGridReady: registerToolbarGrid } = useAGGridToolbar();

  const [labelsTarget, setLabelsTarget] = useState<UserTableV2Row | null>(null);
  const [bulkAction, setBulkAction] = useState<BulkUserAction | null>(null);
  const [updatingUserIds, setUpdatingUserIds] = useState<
    Set<UserTableV2Row['id']>
  >(() => new Set());
  const previousUpdatingUserIdsRef = useRef<ReadonlySet<UserTableV2Row['id']>>(
    new Set(),
  );

  const setUserUpdating = useCallback<SetUserUpdating>((userId, isUpdating) => {
    setUpdatingUserIds((currentIds) => {
      if (currentIds.has(userId) === isUpdating) {
        return currentIds;
      }

      const nextIds = new Set(currentIds);

      if (isUpdating) {
        nextIds.add(userId);
      } else {
        nextIds.delete(userId);
      }

      return nextIds;
    });
  }, []);

  const debouncedSearch = useDebounce(searchParam ?? '', 300);

  const [usersCount, setUsersCount] = useState<number>();

  const datasource = useMemo(
    () =>
      createUsersDatasource({
        search: debouncedSearch,
        queryClient,
        onUsersCount: setUsersCount,
      }),
    [queryClient, debouncedSearch],
  );

  const { userTagsById, isUserTagsLoading, refetchUserTags } = useUsersTags();

  // Grid API is only available between onGridReady and destroy; this guards every imperative call in one place.
  const getGridApi = useCallback(() => {
    const api = apiRef.current;
    return api && !api.isDestroyed() ? api : null;
  }, []);

  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      apiRef.current = event.api;
      registerToolbarGrid(event);
    },
    [registerToolbarGrid],
  );

  // Rows render before the labels resolve, and AG Grid caches rendered cells, so the Labels column has to be told once the tags arrive.
  useEffect(() => {
    getGridApi()?.refreshCells({
      columns: [USER_COLUMN_ID.labels],
      force: true,
    });
  }, [userTagsById, getGridApi]);

  const getSelectedUserIds = useCallback(
    () =>
      getGridApi()
        ?.getSelectedRows()
        .map((row) => row.id) ?? [],
    [getGridApi],
  );

  // Without purge the rows stay on screen while reloading. A purge would also drop the row count to 1 and send the user back to the first page.
  const refreshCache = useCallback(() => {
    getGridApi()?.refreshServerSide();
  }, [getGridApi]);

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries(
      Auth.listPaginatedUsers({
        params: {},
      }),
    );
    void refetchUserTags();

    refreshCache();
  }, [queryClient, refetchUserTags, refreshCache]);

  useImperativeHandle(ref, () => ({ refresh }), [refresh]);

  const onUsersChange = useCallback(() => {
    refreshCache();
  }, [refreshCache]);

  const {
    getContextMenuItems,
    deleteTarget,
    setDeleteTarget,
    deleteUser,
    handleRoleChange,
  } = useUserRowActions({
    isReadonlyPermission,
    onEditClick,
    onUsersChange,
    setUserUpdating,
  });

  const gridContext = useMemo<UserTableV2GridContext>(
    () => ({
      updatingUserIds,
      setUserUpdating,
      isReadonlyPermission,
      onEditClick,
      onRemoveClick: setDeleteTarget,
      userTagsById,
      isUserTagsLoading,
    }),
    [
      setUserUpdating,
      updatingUserIds,
      isReadonlyPermission,
      onEditClick,
      setDeleteTarget,
      userTagsById,
      isUserTagsLoading,
    ],
  );

  const clearSelection = useCallback(() => {
    getGridApi()?.deselectAll();
  }, [getGridApi]);

  // A deleted user may still be selected, drop it so the toolbar does not keep counting it.
  const onUserDeleted = useCallback(() => {
    if (deleteTarget) {
      getGridApi()?.getRowNode(String(deleteTarget.id))?.setSelected(false);
    }
    onUsersChange();
  }, [deleteTarget, getGridApi, onUsersChange]);

  const onBulkActionComplete = useCallback(() => {
    onUsersChange();
    clearSelection();
  }, [onUsersChange, clearSelection]);

  const { runBulkAction } = useUserBulkActions({
    onComplete: onBulkActionComplete,
  });

  const handleBulkConfirm = useCallback(() => {
    if (!bulkAction || bulkAction === 'editBulk') return;
    runBulkAction({ action: bulkAction, userIds: getSelectedUserIds() });
    setBulkAction(null);
  }, [bulkAction, getSelectedUserIds, runBulkAction]);

  const handleBulkEditSave = useCallback(
    (roleValue: string | number) => {
      const roleId =
        typeof roleValue === 'number' ? roleValue : Number(roleValue);
      runBulkAction({
        action: 'changeRole',
        userIds: getSelectedUserIds(),
        roleId,
      });
      setBulkAction(null);
    },
    [getSelectedUserIds, runBulkAction],
  );

  const { columns: dynamicColumnsConfig, isLoading: areColumnsLoading } =
    useUsersColumns();

  // Reuse the role options that the dynamic columns endpoint already provides for RoleCell, so the bulk edit modal stays consistent with the table's inline role dropdown.
  const roleOptions = useMemo(
    () =>
      dynamicColumnsConfig.find((c) => c.id === USER_COLUMN_ID.role)?.options ??
      [],
    [dynamicColumnsConfig],
  );

  const [hasFirstRender, setHasFirstRender] = useState(false);
  const showSkeleton = areColumnsLoading || !hasFirstRender;

  const columnDefs = useMemo<ColDef<UserTableV2Row>[]>(() => {
    const dynamicColumns = provideUserColumnFields(dynamicColumnsConfig, {
      onViewAllLabels: setLabelsTarget,
      onRoleChange: handleRoleChange,
    });

    const actionsColumn: ColDef<UserTableV2Row> = {
      colId: 'actions',
      headerName: '',
      sortable: false,
      pinned: 'right',
      minWidth: 60,
      maxWidth: 60,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      suppressHeaderContextMenu: true,
      cellRenderer: ActionsCell,
      loadingCellRenderer: ActionsCellSkeleton,
    };

    return [
      ...(isReadonlyPermission ? [] : [selectionColumn]),
      ...dynamicColumns,
      ...(isReadonlyPermission ? [] : [actionsColumn]),
    ];
  }, [dynamicColumnsConfig, handleRoleChange, isReadonlyPermission]);

  // Waiting for the columns ordering from the backend to be fetched... than we apply the ordering
  useEffect(() => {
    if (areColumnsLoading || !gridApi) return;

    getGridApi()?.applyColumnState({
      state: columnDefs.flatMap(({ colId }) => (colId ? [{ colId }] : [])),
      applyOrder: true,
    });
  }, [areColumnsLoading, columnDefs, gridApi, getGridApi]);

  const defaultColDef = useMemo<ColDef<UserTableV2Row>>(
    () =>
      createDefaultColumn({
        minWidth: 120,
        width: 200,
        flex: 1,
        sortable: false,
        resizable: true,
        loadingCellRenderer: LoadingCell,
        suppressMovable: true,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
      }),
    [],
  );

  const startBulkAction = useCallback(
    (action: BulkUserAction) => {
      trackAction(BULK_ACTION_NAMES[action], {
        count: getSelectedUserIds().length,
      });
      setBulkAction(action);
    },
    [getSelectedUserIds],
  );

  const toolbarMainButtons = [
    {
      visibility: 'static' as const,
      label: t('New user'),
      disabled: isReadonlyPermission,
      onClick: () => {
        trackAction('User management: create user');
        // Ended by NewUserModal on the user page.
        startOperation('user.invite', {
          context: { usersManagementAgGrid: true },
        });
        setIsNewModalOpened(true);
      },
    },
  ];

  const toolbarSecondaryButtons = [
    {
      visibility: 'dynamic' as const,
      label: t('Activate'),
      onClick: () => startBulkAction('activate'),
    },
    {
      visibility: 'dynamic' as const,
      label: t('Deactivate'),
      onClick: () => startBulkAction('deactivate'),
    },
    {
      visibility: 'dynamic' as const,
      label: t('Resend invitation'),
      icon: <Send />,
      onClick: () => startBulkAction('sendInvitation'),
    },
  ];

  const toolbarIconButtons = [
    {
      visibility: 'dynamic' as const,
      icon: <Edit />,
      tooltip: t('Edit'),
      onClick: () => startBulkAction('editBulk'),
    },
    {
      visibility: 'dynamic' as const,
      color: 'warning' as const,
      icon: <TrashCan />,
      tooltip: t('Remove from workspace'),
      onClick: () => startBulkAction('remove'),
    },
  ];

  // rowSelection always adds its own selection column; checkboxes:false hides
  // the checkbox, not the column. We render our own, so collapse this one to 0.
  const selectionColumnDef = useMemo(
    () => ({ width: 0, minWidth: 0, maxWidth: 0, resizable: false }),
    [],
  );

  const getRowId = useCallback(
    (params: GetRowIdParams<UserTableV2Row>) => String(params.data.id),
    [],
  );

  // Exclude the current user so they can't select/bulk-action themselves.
  const rowSelection = useMemo(
    () => createRowSelection(profile?.id),
    [profile?.id],
  );

  useEffect(() => {
    const api = getGridApi();
    if (!api) return;

    // Swapping the datasource already resets and reloads the rows, so no refresh here.
    api.deselectAll();
    api.paginationGoToFirstPage();
  }, [datasource, getGridApi]);

  useEffect(() => {
    const api = getGridApi();
    const previousUpdatingUserIds = previousUpdatingUserIdsRef.current;

    previousUpdatingUserIdsRef.current = updatingUserIds;
    if (!api) return;

    api.setGridOption('context', gridContext);

    const changedRowNodes = Array.from(
      new Set([...previousUpdatingUserIds, ...updatingUserIds]),
    ).flatMap((userId) => {
      const didUpdatingStateChange =
        previousUpdatingUserIds.has(userId) !== updatingUserIds.has(userId);
      const node = didUpdatingStateChange
        ? api.getRowNode(String(userId))
        : undefined;

      return node ? [node] : [];
    });

    if (changedRowNodes.length) {
      api.refreshCells({ rowNodes: changedRowNodes, force: true });
    }
  }, [gridContext, updatingUserIds, getGridApi]);

  return (
    <>
      <div className="flex h-[calc(100vh-211px)] flex-col gap-4">
        <div className="relative grow overflow-hidden">
          <div
            className={cn('flex h-full flex-col', showSkeleton && 'invisible')}
          >
            <AGGridToolbar
              className="shrink-0"
              gridApi={gridApi}
              mainButton={toolbarMainButtons}
              secondaryButton={toolbarSecondaryButtons}
              iconButton={toolbarIconButtons}
              searchBar={{
                visibility: 'both',
                placeholder: t('Search'),
                queryParamKey: 'search',
              }}
            />
            <AgGridReact
              className="permissions-users-grid min-h-0 grow"
              rowModelType="serverSide"
              loading={false}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              serverSideDatasource={datasource}
              getRowId={getRowId}
              rowSelection={rowSelection}
              selectionColumnDef={selectionColumnDef}
              pagination
              paginationPageSize={USER_TABLE_V2_PAGE_SIZE}
              paginationPageSizeSelector={false}
              // A single page needs no pagination controls.
              suppressPaginationPanel={
                usersCount === undefined ||
                usersCount <= USER_TABLE_V2_PAGE_SIZE
              }
              serverSideInitialRowCount={USER_TABLE_V2_PAGE_SIZE}
              suppressServerSideFullWidthLoadingRow
              cacheBlockSize={USER_TABLE_V2_CACHE_BLOCK_SIZE}
              maxBlocksInCache={1}
              blockLoadDebounceMillis={200}
              rowBuffer={5}
              suppressCellFocus
              tooltipShowDelay={0}
              noRowsOverlayComponent={NoResultsOverlay}
              maintainColumnOrder
              context={gridContext}
              getContextMenuItems={getContextMenuItems}
              onGridReady={onGridReady}
              onFirstDataRendered={() => setHasFirstRender(true)}
            />
          </div>
          {showSkeleton ? (
            <div className="absolute inset-0">
              <TableSkeleton />
            </div>
          ) : null}
        </div>
      </div>

      {deleteTarget ? (
        <DeleteUserConfirmationDialog
          target={deleteTarget}
          deleteUser={deleteUser}
          onClose={() => setDeleteTarget(null)}
          onDeleted={onUserDeleted}
        />
      ) : null}

      {labelsTarget ? (
        <LabelModal
          onClose={() => setLabelsTarget(null)}
          userProfile={{
            ...labelsTarget,
            tags: userTagsById.get(labelsTarget.id) ?? [],
          }}
          isActive={!!labelsTarget}
          tabs={tabs ?? []}
        />
      ) : null}

      {bulkAction && bulkAction !== 'editBulk' ? (
        <BulkActionConfirmationDialog
          action={bulkAction}
          onClose={() => setBulkAction(null)}
          onConfirm={handleBulkConfirm}
        />
      ) : null}

      <BulkEditModal
        open={bulkAction === 'editBulk'}
        roleOptions={roleOptions}
        onClose={() => setBulkAction(null)}
        onSave={handleBulkEditSave}
      />
    </>
  );
};

export default UserTableV2;
